export const COLLECTION_SYNC_FREQUENCY = Object.freeze({
  Off: 'off',
  Daily: 'daily',
  Weekly: 'weekly',
  Monthly: 'monthly',
});

export const COLLECTION_EXCLUDED_URL_REAPPEAR_BEHAVIOR = Object.freeze({
  KeepExcluded: 'keep_excluded',
  AddIfMatchesSyncCriteria: 'add_if_matches_sync_criteria',
});

export const COLLECTION_MISSING_INCLUDED_URL_BEHAVIOR = Object.freeze({
  KeepInCollection: 'keep_in_collection',
  RemoveFromCollection: 'remove_from_collection',
});

export const COLLECTION_URL_CONDITION_TYPE = Object.freeze({
  Contains: 'contains',
  Regex: 'regex',
  StartsWith: 'startsWith',
  EndsWith: 'endsWith',
});

export const REINDEX_FREQUENCY_OPTIONS = Object.freeze([
  { value: COLLECTION_SYNC_FREQUENCY.Off, label: 'Off' },
  { value: COLLECTION_SYNC_FREQUENCY.Daily, label: 'Daily' },
  { value: COLLECTION_SYNC_FREQUENCY.Weekly, label: 'Weekly' },
  { value: COLLECTION_SYNC_FREQUENCY.Monthly, label: 'Monthly' },
]);

export const URL_CONDITION_TYPE_OPTIONS = Object.freeze([
  { value: COLLECTION_URL_CONDITION_TYPE.Contains, label: 'Contains' },
  { value: COLLECTION_URL_CONDITION_TYPE.StartsWith, label: 'Starts with' },
  { value: COLLECTION_URL_CONDITION_TYPE.EndsWith, label: 'Ends with' },
  { value: COLLECTION_URL_CONDITION_TYPE.Regex, label: 'Matches regex' },
]);

export const NEW_URL_MODE = Object.freeze({
  AddAll: 'add_all',
  MatchFilter: 'match_filter',
});

export const createEmptyUrlCondition = () => ({
  type: COLLECTION_URL_CONDITION_TYPE.Contains,
  value: '',
  isNegated: false,
});

export const URL_CONDITION_PLACEHOLDERS = Object.freeze({
  [COLLECTION_URL_CONDITION_TYPE.Contains]: 'e.g. /agenda/',
  [COLLECTION_URL_CONDITION_TYPE.StartsWith]: 'e.g. https://example.com/en/',
  [COLLECTION_URL_CONDITION_TYPE.EndsWith]: 'e.g. .html',
  [COLLECTION_URL_CONDITION_TYPE.Regex]: 'e.g. /blog/.+',
});

const matchUrlCondition = (url, condition) => {
  const value = String(condition?.value || '').trim();
  if (!value) return false;

  const target = String(url || '');
  const type = condition?.type || COLLECTION_URL_CONDITION_TYPE.Contains;

  let matched = false;
  if (type === COLLECTION_URL_CONDITION_TYPE.StartsWith) {
    matched = target.startsWith(value);
  } else if (type === COLLECTION_URL_CONDITION_TYPE.EndsWith) {
    matched = target.endsWith(value);
  } else if (type === COLLECTION_URL_CONDITION_TYPE.Regex) {
    try {
      matched = new RegExp(value, 'i').test(target);
    } catch {
      matched = false;
    }
  } else {
    matched = target.toLowerCase().includes(value.toLowerCase());
  }

  return condition?.isNegated ? !matched : matched;
};

/**
 * Include if every non-empty rule matches (AND). With no rules, everything is included.
 */
export const urlMatchesFilterConditions = (url, conditions = []) => {
  const activeConditions = (conditions || []).filter(condition => condition?.value?.trim());
  if (!activeConditions.length) return true;
  return activeConditions.every(condition => matchUrlCondition(url, condition));
};

export const getDefaultSyncSettings = () => ({
  isActive: false,
  frequency: COLLECTION_SYNC_FREQUENCY.Daily,
  excludedUrlReappearBehavior: COLLECTION_EXCLUDED_URL_REAPPEAR_BEHAVIOR.KeepExcluded,
  missingIncludedUrlBehavior: COLLECTION_MISSING_INCLUDED_URL_BEHAVIOR.KeepInCollection,
  newUrlCriteria: {
    conditions: [],
  },
  lastSyncDate: null,
  lastSyncStatus: 'not_started',
  lastSyncError: null,
});

export const getReindexFrequencyValue = syncSettings => {
  if (!syncSettings?.isActive) return COLLECTION_SYNC_FREQUENCY.Off;
  return syncSettings.frequency || COLLECTION_SYNC_FREQUENCY.Daily;
};

export const getNewUrlMode = syncSettings => {
  const conditions = syncSettings?.newUrlCriteria?.conditions || [];
  return conditions.length ? NEW_URL_MODE.MatchFilter : NEW_URL_MODE.AddAll;
};

export const buildSyncSettingsPayload = draft => {
  const reindexFrequency = draft.reindexFrequency || COLLECTION_SYNC_FREQUENCY.Off;
  const isActive = reindexFrequency !== COLLECTION_SYNC_FREQUENCY.Off;
  const conditions =
    draft.newUrlMode === NEW_URL_MODE.MatchFilter
      ? (draft.conditions || []).filter(condition => condition.value?.trim())
      : [];

  return {
    isActive,
    frequency: isActive ? reindexFrequency : COLLECTION_SYNC_FREQUENCY.Daily,
    excludedUrlReappearBehavior: draft.excludedUrlReappearBehavior,
    missingIncludedUrlBehavior: draft.missingIncludedUrlBehavior,
    newUrlCriteria: {
      conditions: conditions.map(condition => ({
        type: condition.type,
        value: condition.value.trim(),
        isNegated: Boolean(condition.isNegated),
      })),
    },
  };
};

export const createSyncSettingsDraft = syncSettings => {
  const source = syncSettings || getDefaultSyncSettings();
  return {
    reindexFrequency: getReindexFrequencyValue(source),
    newUrlMode: getNewUrlMode(source),
    conditions: (source.newUrlCriteria?.conditions || []).map(condition => ({
      type: condition.type || COLLECTION_URL_CONDITION_TYPE.Contains,
      value: condition.value || '',
      isNegated: Boolean(condition.isNegated),
    })),
    excludedUrlReappearBehavior:
      source.excludedUrlReappearBehavior || COLLECTION_EXCLUDED_URL_REAPPEAR_BEHAVIOR.KeepExcluded,
    missingIncludedUrlBehavior:
      source.missingIncludedUrlBehavior ||
      COLLECTION_MISSING_INCLUDED_URL_BEHAVIOR.KeepInCollection,
  };
};
